import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Base URL ของ API — เรียกตอนใช้งาน (อย่า cache ตอน load module)
 * Production บนโดเมนจริง: ใช้ same-origin (ผ่าน nginx /api → backend)
 * Local: NEXT_PUBLIC_API_URL หรือ http://localhost:8000
 */
export function getApiUrl(): string {
  if (typeof window !== "undefined") {
    const o = window.location.origin;
    if (o.includes("localhost") || o.startsWith("http://127.0.0.1")) {
      return process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";
    }
    return o;
  }
  return process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";
}

/**
 * ใช้ใน template string ได้: `${API_URL}/api/...`
 * ทุกครั้งที่แปลงเป็น string จะเรียก getApiUrl() ใหม่ — กันค่าค้างจาก SSR
 */
export const API_URL = {
  toString: () => getApiUrl(),
  valueOf: () => getApiUrl(),
  [Symbol.toPrimitive]: () => getApiUrl(),
} as unknown as string;

/** อ่าน body เป็น JSON; ถ้าได้ HTML (เช่น หน้า Next.js / 502) จะ throw ข้อความชัดเจน */
export async function readApiJson<T = unknown>(res: Response): Promise<T> {
  const text = await res.text();
  const trimmed = text.trim();
  if (!trimmed) return {} as T;
  if (trimmed.startsWith("<!") || trimmed.startsWith("<html") || trimmed.startsWith("<HTML")) {
    throw new Error(
      `API ตอบกลับเป็น HTML แทน JSON (HTTP ${res.status}) — ตรวจว่า /api ชี้ไป backend และ NEXT_PUBLIC_API_URL=https://assets.tpa.or.th`,
    );
  }
  try {
    return JSON.parse(trimmed) as T;
  } catch {
    throw new Error(`API ตอบกลับไม่ใช่ JSON (HTTP ${res.status})`);
  }
}

export function getAuthHeaders(): HeadersInit {
  if (typeof window === "undefined") return {};
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const THAI_MONTHS = [
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
] as const;

function thaiLongFromGregorianParts(year: number, monthIndex: number, day: number): string {
  if (monthIndex < 0 || monthIndex > 11 || day < 1 || day > 31) return "-";
  return `${day} ${THAI_MONTHS[monthIndex]} ${year + 543}`;
}

/**
 * แสดงวันที่เป็นภาษาไทย เช่น 24 มีนาคม 2569
 * รับค่า ISO date (YYYY-MM-DD) หรือ datetime จาก API ได้ — การเก็บในฐานข้อมูลยังเป็นแบบเดิม
 */
export function formatThaiDateLong(value: string | Date | null | undefined): string {
  if (value == null || value === "") return "-";
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "-";
    return thaiLongFromGregorianParts(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const str = String(value).trim();
  const head = str.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(head)) {
    const y = parseInt(head.slice(0, 4), 10);
    const m0 = parseInt(head.slice(5, 7), 10) - 1;
    const d = parseInt(head.slice(8, 10), 10);
    if (!Number.isFinite(y) || !Number.isFinite(m0) || !Number.isFinite(d)) return "-";
    return thaiLongFromGregorianParts(y, m0, d);
  }
  const dt = new Date(str);
  if (Number.isNaN(dt.getTime())) return "-";
  return thaiLongFromGregorianParts(dt.getFullYear(), dt.getMonth(), dt.getDate());
}

/** วันที่แบบ d/m/yyyy สำหรับฟอร์มพิมพ์ เช่น 18/5/2026 */
export function formatDateSlash(value: string | Date | null | undefined): string {
  if (value == null || value === "") return "";
  const toParts = (y: number, m0: number, d: number) => `${d}/${m0 + 1}/${y}`;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";
    return toParts(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const str = String(value).trim();
  const head = str.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(head)) {
    const y = parseInt(head.slice(0, 4), 10);
    const m0 = parseInt(head.slice(5, 7), 10) - 1;
    const d = parseInt(head.slice(8, 10), 10);
    if (!Number.isFinite(y) || !Number.isFinite(m0) || !Number.isFinite(d)) return "";
    return toParts(y, m0, d);
  }
  const dt = new Date(str);
  if (Number.isNaN(dt.getTime())) return "";
  return toParts(dt.getFullYear(), dt.getMonth(), dt.getDate());
}
